import { useEffect, useMemo } from 'react';

import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { ListLevels } from '../../queries/get-list-levels';
import { FilterBarSkeleton } from '../skeletons/FilterBarSkeleton';

import { LevelFilter } from './level-filter';
import { SiteFilter } from './site-filter';

export type WarrantyFiltersProps = {
  filters: IWarrantyRepository.Filters;
  onChange: (filters: IWarrantyRepository.Filters) => void;
};
export function FilterBar({ filters, onChange }: WarrantyFiltersProps) {
  const { data: listLevelsData, isLoading: isLoadingListLevels } =
    ListLevels.useQuery({});

  const me = useMe();
  const asset = useCurrentAsset();

  // Determine display levels: remove `system` / `pcs`, sort by canonical hierarchy.
  const orderedLevels = useMemo(() => {
    const excludeLevels = ['system', 'pcs'];

    // TEMP FIX: Exclude 'pack', 'node' from levels for 69
    if (me.config.id === '69') {
      excludeLevels.push('pack', 'node');
    } else if (me.config.id === '75') {
      if (asset?.asset?.id === '26254085168192602') {
        excludeLevels.push('container', 'pack', 'node');
      } else {
        excludeLevels.push('array', 'pack');
      }
    }

    const lvls =
      listLevelsData?.levels.filter(
        (l) => !excludeLevels.includes(l.accureName),
      ) ?? [];

    return lvls.sort(
      (a, b) =>
        IWarrantyRepository.BATTERY_LEVELS.indexOf(
          a.accureName as IWarrantyRepository.BatteryLevel,
        ) -
        IWarrantyRepository.BATTERY_LEVELS.indexOf(
          b.accureName as IWarrantyRepository.BatteryLevel,
        ),
    );
  }, [listLevelsData, me.config.id, asset?.asset?.id]);

  function updateFilters(newFilters: Partial<IWarrantyRepository.Filters>) {
    onChange({ ...filters, ...newFilters });
  }

  // Compute aggregation level whenever filters or orderedLevels change
  const aggregationLevel = useMemo(() => {
    // find deepest selected level index in orderedLevels
    const deepestIdx = orderedLevels.reduce((max, lvl, idx) => {
      return filters[`${lvl.accureName}Id` as keyof IWarrantyRepository.Filters]
        ? idx
        : max;
    }, -1);

    if (deepestIdx === -1)
      return me.config.id === '75'
        ? asset?.asset?.id === '26254085168192602'
          ? 'array'
          : 'container'
        : 'array';

    const nextIdx = Math.min(deepestIdx + 1, orderedLevels.length - 1);
    return orderedLevels[nextIdx]
      .accureName as IWarrantyRepository.BatteryLevel;
  }, [filters, orderedLevels, me.config.id, asset?.asset?.id]);

  // ensure aggregationLevel in filters (avoid state update during render)
  useEffect(() => {
    if (filters.aggregationLevel !== aggregationLevel) {
      onChange({ ...filters, aggregationLevel });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregationLevel]);

  const featureFlag = useFeatureFlag();

  if (isLoadingListLevels) {
    return <FilterBarSkeleton />;
  }

  return (
    <div className="flex flex-row gap-4">
      {!featureFlag('newNavigation') ? (
        <div className="min-w-52 max-w-64">
          <SiteFilter
            filters={filters}
            onChange={(siteId) => {
              updateFilters({ siteId });
            }}
          />
        </div>
      ) : null}
      {orderedLevels.map((lvl) => (
        <div key={lvl.accureName} className="min-w-52 max-w-64">
          <LevelFilter
            level={lvl.accureName as IWarrantyRepository.BatteryLevel}
            customerLabel={lvl.customerName}
            filters={filters}
            onChange={(partial) => {
              // When a higher-level selection changes, clear lower-level filters for consistency
              const levelOrder = [
                'array',
                'container',
                'bank',
                'pack',
                'module',
                'cell',
              ];
              const changedLevel = Object.keys(partial)[0] || '';

              const idx = levelOrder.findIndex(
                (l) => `${l}Id` === changedLevel,
              );

              const cleared: Partial<IWarrantyRepository.Filters> = {};
              levelOrder.slice(idx + 1).forEach((l) => {
                const key = `${l}Id` as keyof IWarrantyRepository.Filters;
                cleared[key] = undefined;
              });

              updateFilters({ ...cleared, ...partial });
            }}
          />
        </div>
      ))}
    </div>
  );
}
